import { motion } from "framer-motion";
import { ArrowDownRight, Check, PackageCheck, Truck } from "lucide-react";
import { scrollTo } from "@/components/Navbar";

const EASE = [0.16, 1, 0.3, 1];

const PATHWAYS = [
  {
    id: "carrier",
    number: "01",
    title: "CARRIERS",
    icon: Truck,
    description:
      "Partner with Merchant Alliance Brokerage to find freight suited to available equipment, operating regions, and preferred lanes.",
    details: [
      "Owner-operators and small-to-midsize fleets",
      "Dry van, reefer, flatbed, stepdeck, power-only, specialized, box truck, and hotshot",
      "Active authority, current insurance, safe operations, and dependable communication",
      "Regional, dedicated, repeat, and over-the-road opportunities matched to your operation",
    ],
    button: "CARRIERS — CLICK HERE",
    target: "#contact",
    tone: "orange",
  },
  {
    id: "shipper",
    number: "02",
    title: "SHIPPERS",
    icon: PackageCheck,
    description:
      "Work with Merchant Alliance Brokerage for dependable freight coordination, equipment matching, shipment communication, and direct dispatch support.",
    details: [
      "Manufacturers, distributors, wholesalers, produce, construction, and automotive businesses",
      "Full truckload and less-than-truckload shipments",
      "Dry van, temperature-controlled, flatbed, specialized, and expedited freight",
      "Direct communication, equipment matching, coordination, and delivery visibility",
    ],
    button: "SHIPPERS — CLICK HERE",
    target: "#contact",
    tone: "amber",
  },
];

const PathwayCard = ({ pathway, index }) => {
  const Icon = pathway.icon;
  const isCarrier = pathway.tone === "orange";

  return (
    <motion.article
      id={pathway.id === "shipper" ? "shippers" : undefined}
      data-testid={`audience-pathway-${pathway.id}-card`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: EASE }}
      className="group relative flex min-h-[520px] scroll-mt-24 flex-col overflow-hidden border border-[#222736] bg-[#12141C] p-6 transition-[border-color,background-color] duration-500 hover:border-[#383F56] hover:bg-[#151821] sm:p-8"
    >
      <div
        className={`absolute inset-x-0 top-0 h-1 ${
          isCarrier ? "bg-[#FF5500]" : "bg-[#F59E0B]"
        }`}
      />
      <div className="flex items-start justify-between gap-6">
        <span
          className={`flex h-12 w-12 items-center justify-center border ${
            isCarrier
              ? "border-[#FF5500]/40 bg-[#FF5500]/10 text-[#FF5500]"
              : "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"
          }`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <span className="font-mono text-xs font-bold tracking-[0.2em] text-slate-600">
          PATH // {pathway.number}
        </span>
      </div>

      <h2 className="mt-8 font-display text-4xl font-black uppercase text-white sm:text-5xl">
        {pathway.title}
      </h2>
      <p className="mt-4 max-w-xl text-base leading-7 text-slate-300 sm:text-[17px]">
        {pathway.description}
      </p>

      <div className="mt-8 border-t border-[#222736] pt-6">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          Partnership Fit
        </p>
        <ul data-testid={`audience-pathway-${pathway.id}-details`} className="mt-4 space-y-3">
          {pathway.details.map((detail) => (
            <li key={detail} className="grid grid-cols-[16px_1fr] gap-3 text-[15px] leading-7 text-slate-200 sm:text-base">
              <Check className={`mt-1 h-3.5 w-3.5 ${isCarrier ? "text-[#FF5500]" : "text-[#F59E0B]"}`} />
              <span>{detail}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto pt-8">
        <button
          data-testid={`audience-pathway-${pathway.id}-cta`}
          onClick={() => scrollTo(pathway.target)}
          className="flex min-h-14 w-full items-center justify-between gap-4 border border-[#4C5F84] bg-[#1A1D28] px-5 py-4 font-mono text-xs font-bold tracking-[0.14em] text-[#B9C9E6] transition-[background-color,border-color,color] duration-300 hover:border-[#6E87B4] hover:bg-[#222736] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#6E87B4] focus:ring-offset-2 focus:ring-offset-[#0A0B0E]"
        >
          <span>{pathway.button}</span>
          <ArrowDownRight className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
        </button>
      </div>
    </motion.article>
  );
};

export default function AudiencePathways() {
  return (
    <section id="pathways" data-testid="audience-pathways-section" className="relative scroll-mt-20 border-y border-[#222736] py-20 md:py-28">
      <div className="grid-lines absolute inset-0 opacity-20" />
      <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-12 flex flex-wrap items-end justify-between gap-5"
        >
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#F59E0B]">
              START HERE // CHOOSE YOUR PATH
            </p>
            <h2 className="mt-4 font-display text-3xl font-black uppercase text-white sm:text-5xl">
              Built For Both Sides Of Freight
            </h2>
          </div>
          <p className="max-w-sm text-base leading-7 text-slate-400">
            Select the relationship that fits your business to reach the right place quickly.
          </p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-2 md:gap-8">
          {PATHWAYS.map((pathway, index) => (
            <PathwayCard key={pathway.id} pathway={pathway} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}